import React, { useState, useEffect } from "react";
import { useUpdateRequestStatusMutation } from "../api/starterKitApi";
import { showToast } from "../../../core/utils/alert";
import { getApiErrorMessage } from "../../../core/utils/errorHandler";

interface UpdateRequestStatusModalProps {
  initialData: any;
  onClose: () => void;
}

const UpdateRequestStatusModal: React.FC<UpdateRequestStatusModalProps> = ({ initialData, onClose }) => {
  const [status, setStatus] = useState<'approved' | 'rejected'>('approved');
  const [reviewerNote, setReviewerNote] = useState("");

  const [updateRequestStatus, { isLoading }] = useUpdateRequestStatusMutation();

  useEffect(() => {
    if (initialData) {
      setStatus('approved');
      setReviewerNote("");
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!initialData) return;

    try {
      const response = await updateRequestStatus({
        id: initialData.id,
        status,
        reviewer_note: reviewerNote,
      }).unwrap();

      if (response.success) {
        showToast({ title: "Success", text: response.message, icon: "success" });
        document.getElementById("close_update_request_status")?.click();
        onClose();
      } else {
        showToast({ title: "Error", text: response.message || "Failed to update request", icon: "error" });
      }
    } catch (error: any) {
      showToast({ title: "Error", text: getApiErrorMessage(error) || "Something went wrong", icon: "error" });
    }
  };

  return (
    <div className="modal fade" id="update_request_status_modal" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Review Request</h5>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              id="close_update_request_status"
              onClick={onClose}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <p><strong>Center:</strong> {initialData?.center?.name}</p>
                <p><strong>Course:</strong> {initialData?.course?.name}</p>
                <p><strong>Quantity:</strong> {initialData?.quantity}</p>
              </div>
              <div className="mb-3">
                <label className="form-label">Action <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e: any) => setStatus(e.target.value)}
                >
                  <option value="approved">Approve & Dispatch Stock</option>
                  <option value="rejected">Reject</option>
                </select>
                {status === 'approved' && (
                  <small className="text-muted mt-1 d-block">
                    Approving this request will automatically deduct stock from the Main Center and add it to the Center's inventory.
                  </small>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Reviewer Note (Optional)</label>
                <textarea
                  className="form-control"
                  value={reviewerNote}
                  onChange={(e) => setReviewerNote(e.target.value)}
                  rows={3}
                  placeholder="Add any comments..."
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? "Submitting..." : "Submit Review"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateRequestStatusModal;
